// One school's free read, as a card (#2549; shared since #3480).
//
// Astra Lite draws it under the picture; signed-in Astra draws the same card
// for a read carried in at sign-in (ConciergeChat, mock B2), so the read a
// student unlocked looks the same on both sides of the sign-in.
//
// `read` is the server's: a FULL read (match, drivers, major note) or, with
// nobody signed in, the LOCKED half
// (astra_read_unlock.lock_read: band, rate, test policy, band_line). The card
// draws only what it is given, so the locked half can never show a reason:
// the server never sent one.
import React from 'react';
import { rateLine } from '../../../utils/rateBasis';
import { POLICY_WORDS } from '../../../content/astraLite';
import { TONE_STYLE, driverTones, majorNoteTone } from '../../../utils/readTone';
import { BAND_CHIP_UNKNOWN } from '../../../utils/fitBands';
import { REPLAY_PRIVATE_CLASS } from '../../../services/analyticsPrivacy';

const BAND_CHIP = {
    Safety: 'bg-[#E8F0E9] text-[#1A4D2E] border-[#CFE0D2]',
    Target: 'bg-[#1A4D2E] text-white border-[#1A4D2E]',
    Reach: 'bg-[#FCEEE8] text-[#C05838] border-[#F0D5C8]',
};

// One reason line, coloured by the tone the server decided (#3573). A concern
// is said in words too, for a reader who can't see the red.
export function ReasonLine({ tone = 'neutral', testid, children }) {
    const style = TONE_STYLE[tone] || TONE_STYLE.neutral;
    return (
        <li data-testid={testid} data-tone={tone} className={`flex gap-2 text-sm ${style.text}`}>
            <span className={style.marker} aria-hidden="true">▸</span>
            {tone === 'concern' && <span className="sr-only">Worth knowing: </span>}
            <span>{children}</span>
        </li>
    );
}

export default function ReadCard({ read, testid = 'try-hero-read' }) {
    const policy = POLICY_WORDS[read.testing_policy];
    const tones = driverTones(read);
    return (
        // Blocked in replay (#4475): its lines quote the GPA the visitor typed.
        <div data-testid={testid} className={`${REPLAY_PRIVATE_CLASS} rounded-xl border border-[#E0DED8] bg-[#FDFCF7] p-5 text-left`}>
            <div className="flex flex-wrap items-center gap-3">
                <span className="font-serif text-lg font-bold text-[#2C2C2C]">{read.name}</span>
                <span className={`rounded-full border px-3 py-0.5 text-sm font-semibold ${BAND_CHIP[read.band] || BAND_CHIP_UNKNOWN}`}>
                    {read.band} for you{read.match ? ` · ${read.match}%` : ''}
                </span>
                {read.acceptance_rate != null && (
                    // Its basis, said (#3529): a resident is read on the in-state rate,
                    // which a bare "acceptance rate" passed off as the overall one.
                    <span className="text-xs text-[#6B6B6B]">
                        {rateLine(read.acceptance_rate, read.acceptance_basis)}{policy ? ` · ${policy}` : ''}
                    </span>
                )}
            </div>
            {read.band_line && (
                <p data-testid="astra-band-line" className="mt-3 text-sm leading-relaxed text-[#4A4A4A]">{read.band_line}</p>
            )}
            {((read.drivers || []).length > 0 || read.major_note) && (
                <ul className="mt-3 space-y-1.5" data-testid="read-reasons">
                    {(read.drivers || []).map((d, i) => (
                        <ReasonLine key={d} tone={tones[i]} testid="read-driver">{d}</ReasonLine>
                    ))}
                    {read.major_note && (
                        <ReasonLine tone={majorNoteTone(read)} testid="read-major-note">{read.major_note}</ReasonLine>
                    )}
                </ul>
            )}
        </div>
    );
}
